import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { AuthPage } from '@refinedev/core';

import {
  Button,
  Loading,
  Typography,
  useSnackbar,
} from '@rrexpress/core';

import BluebirdImage from '@/assets/images/bluebird.svg';
import { BaseForm } from '@/components/form/form';
import FormField from '@/components/form/form-field';
import { EyeCloseIcon, EyeIcon } from '@/components/icons';
import { useLoginApplication } from '@/modules/auth/application/login';
import classJoin from '@/utility/helper/classJoin';
import { RenderIf } from '@/components/conditional/render-if';
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { ILoginPayload } from '@/modules/auth/model/login';

const Login: React.FC = () => {
  const { loginApp, loadingLogin } = useLoginApplication();
  const setTitle = useDocumentTitle();
  const { show } = useSnackbar();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  async function onSubmit(values: ILoginPayload) {
    try {
      await loginApp(values);
    } catch (error: any) {
      show(error?.message, 'error');
    }
  }

  const Footer = () => (
    <Button
      variant="primary"
      className="w-full"
    >
      Login
    </Button>
  );

  useEffect(() => {
    setTitle('Login | Bluebird Kirim');

    return () => {
      setTitle('Bluebird Kirim');
    };
  }, []);

  return (
    <AuthPage
      type="login"
      renderContent={() => (
        <div
          className={
            classJoin(
              'mx-auto h-[100vh] flex flex-col items-center justify-center',
              loadingLogin ? 'bg-white' : 'bg-[#EEF2F5]',
            )
          }
        >
          <div className="p-8 lg:min-w-[25vw] bg-white rounded-lg">
            <RenderIf
              condition={!loadingLogin}
              fallback={(
                <Loading
                  variant="circular"
                  size={40}
                  speed={0.6}
                  indicatorCap="round"
                />
              )}
            >
              <div className="flex flex-col items-center justify-center">
                <img src={BluebirdImage} className="w-[144px] mx-auto mb-4" />
                <div className="mb-4">
                  <Typography level={2} fontWeight="medium">Selamat Datang!</Typography>
                </div>
                <div className="mb-4">
                  <Typography level={6} fontWeight="medium">Di <span className="text-primary">Bluebird</span> Transport Management System</Typography>
                </div>
              </div>
              <BaseForm
                onFinish={onSubmit}
                footer={<Footer />}
              >
                <div className="mb-4 flex flex-col gap-3">
                  <Typography level={5}>
                    <label htmlFor="username" className="font-medium">Username</label>
                  </Typography>
                  <div className="w-full">
                    <FormField
                      component="form"
                      name="username"
                      componentProps={{
                        type: 'username',
                        label: 'Username',
                        required: true,
                      }}
                    />
                  </div>
                </div>

                <div className="mb-4 flex flex-col gap-3">
                  <Typography level={5}>
                    <label htmlFor="password" className="font-medium">Password</label>
                  </Typography>
                  <div className="w-full">
                    <FormField
                      component="form"
                      name="password"
                      componentProps={{
                        type: showPassword ? 'text' : 'password',
                        label: 'Password',
                        required: true,
                        rightIcon: (
                          <div onClick={() => setShowPassword((password) => !password)} className="cursor-pointer">
                            {showPassword ? (
                              <EyeIcon />
                            ) : (
                              <EyeCloseIcon />
                            )}
                          </div>
                        ),
                      }}
                    />
                  </div>
                </div>
                <div className="mb-4 flex gap-3 justify-between items-center">
                  <FormField
                    component="checkbox"
                    name="rememberMe"
                    componentProps={{
                      className: '!w-fit',
                      label: 'Ingat saya',
                    }}
                  />
                  <Link to="/auth/forgot-password" className="font-semibold underline text-sm">
                    Reset Password
                  </Link>
                </div>
              </BaseForm>
            </RenderIf>
          </div>
        </div>
      )}
    />
  );
};

export default Login;
